import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import InitiateSrConfirmationModal from '../tracker/modals/InitiateSrConfirmation';
import {
  clearShowSrConfirmationQueue,
  setShowSrConfirmationQueue
} from '../../redux/actions/genericActions';
import { MarkServiceRequestInitiated } from '../../api/selfserve';
import {
  normalizeSrConfirmationQueue,
  SrConfirmationQueueData,
} from '../../utils/serviceRequestConfirmation';
import { cleverTapTrigger } from '../../tracking/segment';

const SHOW_SR_CONFIRMATION_STORAGE_KEY = 'show_sr_confirmation_queue';
const EMPTY_SR_CONFIRMATION_QUEUE: SrConfirmationQueueData = {
  refNos: [],
  serviceRequestLabel: '',
};

const readStoredQueue = (): SrConfirmationQueueData => {
  if (typeof window === 'undefined') {
    return EMPTY_SR_CONFIRMATION_QUEUE;
  }

  try {
    const rawValue = window.sessionStorage.getItem(SHOW_SR_CONFIRMATION_STORAGE_KEY);
    if (!rawValue) {
      return EMPTY_SR_CONFIRMATION_QUEUE;
    }
    const parsedValue = JSON.parse(rawValue);
    return normalizeSrConfirmationQueue(parsedValue);
  } catch (error) {
    return EMPTY_SR_CONFIRMATION_QUEUE;
  }
};

const writeStoredQueue = (queue: SrConfirmationQueueData) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (!queue.refNos.length) {
      window.sessionStorage.removeItem(SHOW_SR_CONFIRMATION_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(SHOW_SR_CONFIRMATION_STORAGE_KEY, JSON.stringify(queue));
  } catch (error) {
    // sessionStorage blocked (in-app webviews, privacy mode). Queue lives in
    // Redux and is repopulated from the server on every page load, so the
    console.warn('SR confirmation queue persistence skipped:', error);
  }
};

const ServiceRequestConfirmationModal = () => {
  const router = useRouter();
  const dispatch: any = useDispatch();
  const hasHydratedFromStorage = useRef(false);
  const [activeSrConfirmation, setActiveSrConfirmation] = useState<SrConfirmationQueueData>(
    EMPTY_SR_CONFIRMATION_QUEUE
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeAction, setActiveAction] = useState<'confirm' | 'cancel' | null>(null);
  const [actionError, setActionError] = useState('');
  const [actionResult, setActionResult] = useState<'confirmed' | 'cancelled' | null>(null);
  const queuedSrConfirmation = normalizeSrConfirmationQueue(useSelector(
    (state: any) => state?.genericReducer?.showSrConfirmationQueue || []
  ));
  const queuedSrConfirmationKey = JSON.stringify(queuedSrConfirmation);
  const isAuthenticated = useSelector(
    (state: any) => state?.customerReducer?.is_authenticate
  );

  const isAllowedRoute =
    router.pathname === '/' ||
    router.pathname.startsWith('/account') ||
    router.asPath.startsWith('/myaccount');

  useEffect(() => {
    if (hasHydratedFromStorage.current || queuedSrConfirmation.refNos.length) {
      return;
    }
    hasHydratedFromStorage.current = true;
    const storedQueue = readStoredQueue();
    if (storedQueue.refNos.length) {
      dispatch(setShowSrConfirmationQueue(storedQueue));
    }
  }, [dispatch, queuedSrConfirmation.refNos.length]);

  useEffect(() => {
    writeStoredQueue(queuedSrConfirmation);
  }, [queuedSrConfirmationKey]);

  useEffect(() => {
    if (!activeSrConfirmation.refNos.length && queuedSrConfirmation.refNos.length > 0) {
      setActiveSrConfirmation(queuedSrConfirmation);
    }
  }, [activeSrConfirmation.refNos.length, queuedSrConfirmationKey]);

  useEffect(() => {
    if (activeSrConfirmation.refNos.length > 0 && isAllowedRoute && isAuthenticated) {
      cleverTapTrigger('SR Confirmation Pop-up Displayed', {
        ref_nos: activeSrConfirmation.refNos.join(','),
        service_request_label: activeSrConfirmation.serviceRequestLabel || '',
      });
    }
  }, [activeSrConfirmation.refNos.length]);

  useEffect(() => {
    if (!activeSrConfirmation.refNos.length && !queuedSrConfirmation.refNos.length) {
      setActionResult(null);
      setActionError('');
      setIsSubmitting(false);
      setActiveAction(null);
    }
  }, [activeSrConfirmation.refNos.length, queuedSrConfirmation.refNos.length]);

  const hasActiveMessage = activeSrConfirmation.refNos.length > 0;

  const onCancel = () => {
    if (!activeSrConfirmation.refNos.length || isSubmitting) {
      return;
    }
    cleverTapTrigger('SR Confirmation - No Clicked', {
      ref_nos: activeSrConfirmation.refNos.join(','),
      service_request_label: activeSrConfirmation.serviceRequestLabel || '',
    });
    setActionError('');
    setIsSubmitting(true);
    setActiveAction('cancel');
    MarkServiceRequestInitiated({ ref_no: activeSrConfirmation.refNos, confirm: false })
      .then((response: any) => {
        const hasSuccessStatus =
          typeof response?.data?.status === 'undefined' || response?.data?.status === true;
        const isSuccess = Boolean(response?.response?.ok) && hasSuccessStatus;
        if (!isSuccess) {
          setActionError('Unable to process your request. Please try again.');
          return;
        }
        dispatch(clearShowSrConfirmationQueue());
        setActionResult('cancelled');
      })
      .catch(error => {
        console.log('Failed to cancel service request initiation', error);
        setActionError('Unable to process your request. Please try again.');
      })
      .finally(() => {
        setIsSubmitting(false);
        setActiveAction(null);
      });
  };

  const onConfirm = () => {
    if (!activeSrConfirmation.refNos.length || isSubmitting) {
      return;
    }
        cleverTapTrigger('SR Confirmation - Yes Clicked', {
          ref_nos: activeSrConfirmation.refNos.join(','),
          service_request_label: activeSrConfirmation.serviceRequestLabel || '',
        });
        setActionError('');
        setIsSubmitting(true);
        setActiveAction('confirm');
        MarkServiceRequestInitiated({
        ref_no: activeSrConfirmation.refNos,
        confirm: true,
      })
      .then((response: any) => {
        const hasSuccessStatus =
          typeof response?.data?.status === 'undefined' || response?.data?.status === true;
        const isSuccess = Boolean(response?.response?.ok) && hasSuccessStatus;
        if (!isSuccess) {
          setActionError('Unable to process your request. Please try again.');
          return;
        }
        dispatch(clearShowSrConfirmationQueue());
        setActionResult('confirmed');
      })
      .catch(error => {
        console.log('Failed to confirm service request', error);
        setActionError('Unable to process your request. Please try again.');
      })
      .finally(() => {
        setIsSubmitting(false);
        setActiveAction(null);
      });
  };

  const closeCurrentSrPopup = () => {
    cleverTapTrigger('SR Confirmation Pop-up Closed', {
      ref_nos: activeSrConfirmation.refNos.join(','),
      service_request_label: activeSrConfirmation.serviceRequestLabel || '',
      action_result: actionResult || 'dismissed',
    });
    dispatch(clearShowSrConfirmationQueue());
    setActiveSrConfirmation(EMPTY_SR_CONFIRMATION_QUEUE);
    setActionResult(null);
    setActionError('');
    setIsSubmitting(false);
    setActiveAction(null);
  };

  if (!hasActiveMessage || !isAllowedRoute || !isAuthenticated) {
    return null;
  }

  return (
    <InitiateSrConfirmationModal
      isOpen={true}
      serviceRequestRefs={activeSrConfirmation.refNos}
      serviceRequestLabel={activeSrConfirmation.serviceRequestLabel || undefined}
      onClose={closeCurrentSrPopup}
      onCancel={onCancel}
      onConfirm={onConfirm}
      isLoading={isSubmitting && activeAction === 'confirm'}
      secondaryButtonLoading={isSubmitting && activeAction === 'cancel'}
      actionResult={actionResult}
      errorMessage={actionError}
      hideCloseButton={actionResult === null}
    />
  );
};

export default ServiceRequestConfirmationModal;
